import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Building2, Power, PowerOff, X, Mail, Save, Trash2, AlertCircle, RotateCcw, AlertTriangle } from "lucide-react";
import { formatDay } from "@/lib/format";
import { formatRelativeTime } from "@/lib/relativeTime";

export default function Superadmin() {
  const [tenants, setTenants] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/admin/tenants"),
      api.get("/admin/tenants", { params: { only_deleted: true } }),
    ]);
    setTenants(a.data);
    setDeleted(b.data);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (t) => {
    if (!window.confirm(`${t.is_active ? "Deactivate" : "Activate"} tenant ${t.tenant_name}?`)) return;
    await api.post(`/admin/tenants/${t.tenant_id}/toggle`);
    load();
  };

  const toggleMulti = async (t) => {
    await api.patch(`/admin/tenants/${t.tenant_id}/multi-pipeline`, {
      multi_pipeline_enabled: !t.multi_pipeline_enabled,
    });
    toast.success(
      `Multi-pipeline ${!t.multi_pipeline_enabled ? "enabled" : "disabled"} for ${t.tenant_name}`
    );
    load();
  };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="cw-caption uppercase tracking-widest">Superadmin</div>
          <h1 className="cw-h1 mt-1">Tenants</h1>
          <p className="text-sm text-[var(--cw-muted)] mt-1">
            Provision new VC firms and manage existing workspaces.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="cw-btn-primary flex items-center gap-2" data-testid="new-tenant-btn">
          <Plus size={16} /> New Tenant
        </button>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {tenants.map((t) => (
          <div key={t.tenant_id} className="cw-card p-5" data-testid={`tenant-card-${t.tenant_slug}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Building2 size={16} style={{ color: "var(--cw-primary)" }} />
                  <h3 className="font-semibold">{t.tenant_name}</h3>
                  {!t.is_active && (
                    <span className="cw-badge" style={{ background: "rgba(0,0,0,0.06)", color: "#666" }}>
                      Inactive
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--cw-muted)] mt-1">
                  {t.tenant_slug} · {t.country || "—"} · {t.subscription_tier}
                </div>
              </div>
              <button onClick={() => toggle(t)}
                className="cw-btn-ghost text-xs inline-flex items-center gap-1"
                data-testid={`toggle-tenant-${t.tenant_slug}`}>
                {t.is_active ? <><PowerOff size={12} /> Deactivate</> : <><Power size={12} /> Activate</>}
              </button>
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2 text-center">
              <Stat label="Users" value={t.user_count ?? 0} />
              <Stat label="Deals" value={t.deal_count ?? 0} />
              <LastLoginStat iso={t.last_login_at} testid={`tenant-last-login-${t.tenant_slug}`} />
              <Stat label="Created" value={formatDay(t.created_at)} small />
            </div>
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className="text-[var(--cw-muted)]">
                Multi-pipeline: <strong>{t.multi_pipeline_enabled ? "On" : "Off"}</strong>
                <span className="mx-2">·</span>
                Notification rules: <strong>{t.notification_rule_count ?? 0}</strong>
                <span className="mx-2">·</span>
                Reports: <strong>{t.email_report_count ?? 0}</strong>
              </span>
            </div>
            <div className="mt-2 flex items-center justify-end gap-2 text-xs">
              <button
                onClick={() => toggleMulti(t)}
                className="cw-btn-ghost text-xs"
                data-testid={`toggle-multi-${t.tenant_slug}`}
              >
                {t.multi_pipeline_enabled ? "Disable multi" : "Enable multi"}
              </button>
              <button
                onClick={() => setDeleteTarget(t)}
                className="cw-btn-ghost text-xs inline-flex items-center gap-1"
                style={{ color: "var(--cw-error)" }}
                data-testid={`soft-delete-${t.tenant_slug}`}
                title="Soft-delete tenant (recoverable for 30 days)"
              >
                <Trash2 size={12} /> Delete
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); window.location.href = `/superadmin/tenants/${t.tenant_id}`; }}
                className="cw-btn-primary text-xs"
                data-testid={`open-workspace-${t.tenant_slug}`}
              >
                Open workspace
              </button>
            </div>
          </div>
        ))}
        {tenants.length === 0 && (
          <div className="col-span-full cw-card p-10 text-center text-[var(--cw-muted)]">
            No tenants yet. Click <strong>New Tenant</strong> to provision one.
          </div>
        )}
      </div>

      {showCreate && (
        <CreateTenantModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {deleteTarget && (
        <SoftDeleteModal
          tenant={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); load(); }}
        />
      )}

      <DeletedTenantsPanel rows={deleted} onChange={load} />

      <SystemEmailsPanel />
    </div>
  );
}

// ===========================================================================
// Soft-delete confirmation modal — type-the-name guard + stats preview.
// ===========================================================================
function SoftDeleteModal({ tenant, onClose, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const nameMatches = typed.trim() === tenant.tenant_name;

  const confirm = async () => {
    if (!nameMatches) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/admin/tenants/${tenant.tenant_id}/soft-delete`,
        { confirm_name: typed.trim() },
      );
      toast.success(
        `"${tenant.tenant_name}" soft-deleted. ${data.users_deactivated} user(s) deactivated. Recoverable for ${data.recovery_window_days} days from the Recycle Bin below.`,
      );
      onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         data-testid="soft-delete-modal">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="px-6 py-4 border-b flex items-start justify-between"
             style={{ borderColor: "var(--cw-border)" }}>
          <div>
            <div className="cw-caption uppercase tracking-widest inline-flex items-center gap-1"
                 style={{ color: "var(--cw-error)" }}>
              <AlertTriangle size={12} /> Danger zone
            </div>
            <h2 className="cw-h2 mt-1">Delete tenant</h2>
          </div>
          <button onClick={onClose} className="text-[var(--cw-muted)]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm">
            This will soft-delete <strong>{tenant.tenant_name}</strong> and all its data.
          </p>
          <ul className="text-sm list-disc pl-5 space-y-1 text-[var(--cw-muted)]">
            <li><strong>{tenant.user_count ?? 0}</strong> active user(s) will be deactivated and can no longer log in.</li>
            <li><strong>{tenant.deal_count ?? 0}</strong> deal(s) and all related data will be hidden immediately.</li>
            <li><strong>{tenant.email_report_count ?? 0}</strong> scheduled email report(s) will be paused.</li>
            <li>Recoverable from the Recycle Bin for <strong>30 days</strong>, after which you can purge permanently.</li>
          </ul>
          <div>
            <div className="cw-caption">Type the tenant name to confirm</div>
            <input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={tenant.tenant_name}
              className="w-full h-9 px-3 rounded-md border text-sm mt-1 font-mono"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid="soft-delete-confirm-input"
            />
          </div>
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2"
             style={{ borderColor: "var(--cw-border)" }}>
          <button onClick={onClose} className="cw-btn-ghost text-sm">Cancel</button>
          <button
            disabled={!nameMatches || submitting}
            onClick={confirm}
            className="text-sm px-4 h-9 rounded-md font-medium text-white disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "var(--cw-error)" }}
            data-testid="soft-delete-confirm-btn"
          >
            {submitting ? "Deleting…" : "Delete tenant"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Recycle Bin — list soft-deleted tenants with Restore + Purge now.
// ===========================================================================
function DeletedTenantsPanel({ rows, onChange }) {
  const [purgeTarget, setPurgeTarget] = useState(null);

  const restore = async (t) => {
    if (!window.confirm(`Restore "${t.tenant_name}"? Users will be reactivated and the workspace will become live again.`)) return;
    try {
      await api.post(`/admin/tenants/${t.tenant_id}/restore`);
      toast.success(`"${t.tenant_name}" restored.`);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    }
  };

  if (!rows || rows.length === 0) return null;

  return (
    <div className="mt-10" data-testid="deleted-tenants-panel">
      <div className="cw-caption uppercase tracking-widest inline-flex items-center gap-1">
        <Trash2 size={12} /> Recycle bin
      </div>
      <h2 className="cw-h2 mt-1">Deleted tenants</h2>
      <p className="text-sm text-[var(--cw-muted)] mt-1">
        Soft-deleted tenants are hidden from the main list and all users are blocked from logging in.
        You can restore within 30 days or purge now to permanently remove every record.
      </p>
      <div className="mt-4 space-y-2">
        {rows.map((t) => (
          <div key={t.tenant_id} className="cw-card p-4 flex items-start justify-between"
               data-testid={`deleted-tenant-row-${t.tenant_slug}`}>
            <div>
              <div className="font-semibold">{t.tenant_name}</div>
              <div className="text-xs text-[var(--cw-muted)] mt-1">
                {t.tenant_slug} · deleted {formatDay(t.deleted_at)} · created {formatDay(t.created_at)}
              </div>
              <div className="text-xs text-[var(--cw-muted)] mt-1">
                {t.user_count ?? 0} user(s) · {t.deal_count ?? 0} deal(s) · {t.email_report_count ?? 0} report(s)
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => restore(t)} className="cw-btn-ghost text-xs inline-flex items-center gap-1"
                      data-testid={`restore-${t.tenant_slug}`}>
                <RotateCcw size={12} /> Restore
              </button>
              <button onClick={() => setPurgeTarget(t)}
                      className="text-xs px-2 py-1 rounded-md inline-flex items-center gap-1 text-white"
                      style={{ background: "var(--cw-error)" }}
                      data-testid={`purge-${t.tenant_slug}`}>
                <Trash2 size={12} /> Purge now
              </button>
            </div>
          </div>
        ))}
      </div>
      {purgeTarget && (
        <PurgeNowModal tenant={purgeTarget}
                       onClose={() => setPurgeTarget(null)}
                       onPurged={() => { setPurgeTarget(null); onChange(); }} />
      )}
    </div>
  );
}

function PurgeNowModal({ tenant, onClose, onPurged }) {
  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const matches = typed.trim() === tenant.tenant_name;

  const confirm = async () => {
    if (!matches) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/admin/tenants/${tenant.tenant_id}/purge-now`,
        { confirm_name: typed.trim() },
      );
      toast.success(
        `"${tenant.tenant_name}" permanently deleted. ${data.total_rows} row(s) removed across ${Object.keys(data.deleted || {}).length} collection(s).`,
      );
      onPurged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Purge failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         data-testid="purge-modal">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="px-6 py-4 border-b flex items-start justify-between"
             style={{ borderColor: "var(--cw-border)" }}>
          <div>
            <div className="cw-caption uppercase tracking-widest inline-flex items-center gap-1"
                 style={{ color: "var(--cw-error)" }}>
              <AlertTriangle size={12} /> Irreversible
            </div>
            <h2 className="cw-h2 mt-1">Purge tenant permanently</h2>
          </div>
          <button onClick={onClose} className="text-[var(--cw-muted)]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm">
            This will <strong>permanently delete</strong> every record for <strong>{tenant.tenant_name}</strong> across all 49 tenant-scoped collections (deals, users, tasks, agent runs, notifications, flow logs, email reports, mappings, pipelines, stages, tags…) and all indirect references.
          </p>
          <p className="text-sm" style={{ color: "var(--cw-error)" }}>
            This action cannot be undone.
          </p>
          <div>
            <div className="cw-caption">Type the tenant name to confirm</div>
            <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)}
                   placeholder={tenant.tenant_name}
                   className="w-full h-9 px-3 rounded-md border text-sm mt-1 font-mono"
                   style={{ borderColor: "var(--cw-border)" }}
                   data-testid="purge-confirm-input" />
          </div>
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2"
             style={{ borderColor: "var(--cw-border)" }}>
          <button onClick={onClose} className="cw-btn-ghost text-sm">Cancel</button>
          <button disabled={!matches || submitting} onClick={confirm}
                  className="text-sm px-4 h-9 rounded-md font-medium text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: "var(--cw-error)" }}
                  data-testid="purge-confirm-btn">
            {submitting ? "Purging…" : "Purge permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// System Emails — GLOBAL mappings for platform-wide account emails.
// welcome_user + forgot_password. Template keys are the aliases published in
// ZeptoMail (case-insensitive). Global uniqueness is enforced by the backend.
// ===========================================================================
function SystemEmailsPanel() {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({}); // { system_email_id: { template_key } }
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v62/system-email-mappings");
      setRows(data || []);
      const d = {};
      (data || []).forEach((r) => { d[r.system_email_id] = { template_key: r.template_key || "" }; });
      setDrafts(d);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (systemEmailId) => {
    const key = (drafts[systemEmailId]?.template_key || "").trim();
    if (!key) {
      toast.error("Template key is required.");
      return;
    }
    setSaving(systemEmailId);
    try {
      await api.post("/v62/system-email-mappings", {
        system_email_id: systemEmailId,
        template_key: key,
      });
      toast.success("Template mapping saved.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save mapping.");
    } finally {
      setSaving(null);
    }
  };

  const clear = async (systemEmailId) => {
    if (!window.confirm(
      "Clear this template mapping? Emails for this system event will not be sent until a new template key is set."
    )) return;
    try {
      await api.delete(`/v62/system-email-mappings/${systemEmailId}`);
      toast.success("Mapping cleared.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to clear mapping.");
    }
  };

  const LABEL = {
    welcome_user: "Welcome user",
    forgot_password: "Forgot password",
  };
  const HELP = {
    welcome_user:
      "Sent to every newly-invited user. Must include the {{set_password_link}} merge tag and {{link_expiry}}=7 days.",
    forgot_password:
      "Sent when a user requests a password reset. Must include the {{reset_link}} merge tag and {{link_expiry}}=30 minutes.",
  };

  return (
    <div className="mt-10" data-testid="system-emails-panel">
      <div className="flex items-start justify-between">
        <div>
          <div className="cw-caption uppercase tracking-widest inline-flex items-center gap-2">
            <Mail size={12} /> Platform-wide
          </div>
          <h2 className="cw-h2 mt-1">System Emails</h2>
          <p className="text-sm text-[var(--cw-muted)] mt-1">
            Map each system event to a published ZeptoMail template alias. These apply to every tenant.
          </p>
        </div>
        <button onClick={load} className="cw-btn-ghost text-xs" data-testid="reload-system-emails-btn">
          Reload
        </button>
      </div>

      <div className="mt-4 cw-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-[var(--cw-muted)]"
                style={{ background: "rgba(0,0,0,0.02)" }}>
              <th className="px-4 py-3">Event</th>
              <th className="px-4 py-3">Template Key (ZeptoMail alias)</th>
              <th className="px-4 py-3">Last Status</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-[var(--cw-muted)]">Loading…</td></tr>
            )}
            {!loading && rows.map((r) => {
              const key = drafts[r.system_email_id]?.template_key ?? "";
              const dirty = key.trim() !== (r.template_key || "");
              const hasError = r.last_status && r.last_status !== "ok" && r.last_status !== null;
              return (
                <tr key={r.system_email_id} className="border-t"
                    style={{ borderColor: "var(--cw-border)" }}
                    data-testid={`system-email-row-${r.system_email_id}`}>
                  <td className="px-4 py-3 align-top">
                    <div className="font-medium">{LABEL[r.system_email_id] || r.system_email_id}</div>
                    <div className="text-xs text-[var(--cw-muted)] mt-1 max-w-md">
                      {HELP[r.system_email_id]}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <input
                      value={key}
                      onChange={(e) => setDrafts((d) => ({
                        ...d,
                        [r.system_email_id]: { template_key: e.target.value },
                      }))}
                      placeholder="e.g. cw_welcome_user_v1"
                      className="w-full h-9 px-3 rounded-md border text-sm font-mono"
                      style={{ borderColor: "var(--cw-border)" }}
                      data-testid={`system-email-input-${r.system_email_id}`}
                    />
                    <div className="text-[11px] text-[var(--cw-muted)] mt-1">
                      Case-insensitive. Must be globally unique across notification, report and system-email mappings.
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    {r.template_key ? (
                      <div className="text-xs">
                        {hasError ? (
                          <span className="inline-flex items-center gap-1" style={{ color: "var(--cw-error)" }}>
                            <AlertCircle size={12} />
                            {r.last_status}
                          </span>
                        ) : (
                          <span className="text-[var(--cw-muted)]">
                            {r.last_status || "not sent yet"}
                          </span>
                        )}
                        {r.last_error_at && (
                          <div className="text-[11px] text-[var(--cw-muted)] mt-1">
                            Last error: {formatDay(r.last_error_at)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="cw-badge" style={{ background: "rgba(0,0,0,0.06)", color: "#666" }}>
                        Not mapped
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right align-top whitespace-nowrap">
                    <button
                      disabled={!dirty || saving === r.system_email_id}
                      onClick={() => save(r.system_email_id)}
                      className="cw-btn-primary text-xs inline-flex items-center gap-1"
                      data-testid={`system-email-save-${r.system_email_id}`}
                    >
                      <Save size={12} />
                      {saving === r.system_email_id ? "Saving…" : "Save"}
                    </button>
                    {r.template_key && (
                      <button
                        onClick={() => clear(r.system_email_id)}
                        className="cw-btn-ghost text-xs ml-2 text-[var(--cw-error)] inline-flex items-center gap-1"
                        data-testid={`system-email-clear-${r.system_email_id}`}
                      >
                        <Trash2 size={12} /> Clear
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, small }) {
  return (
    <div>
      <div className="cw-caption">{label}</div>
      <div className={`font-semibold ${small ? "text-xs" : "text-lg"}`} style={{ color: "var(--cw-primary-dark)" }}>
        {value}
      </div>
    </div>
  );
}

// CW6.2.7: "Last login" stat with exact timestamp on hover tooltip.
function LastLoginStat({ iso, testid }) {
  const { relative, exact } = formatRelativeTime(iso);
  return (
    <div>
      <div className="cw-caption">Last login</div>
      <div
        className="font-semibold text-xs"
        style={{ color: "var(--cw-primary-dark)" }}
        title={exact || "No login recorded"}
        data-testid={testid}
      >
        {relative}
      </div>
    </div>
  );
}

function CreateTenantModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    tenant_name: "", tenant_slug: "",
    country: "India", city: "", subscription_tier: "standard",
    primary_currency: "INR",
    fund_name: "", fund_size_inr: "", vintage_year: 2026,
    admin_email: "", admin_full_name: "", admin_password: "",
    multi_pipeline_enabled: false,
    seed_sample_data: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        fund_size_inr: form.fund_size_inr ? Number(form.fund_size_inr) : null,
        vintage_year: form.vintage_year ? Number(form.vintage_year) : null,
      };
      await api.post("/admin/tenants", payload);
      toast.success(`Tenant "${form.tenant_name}" provisioned`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create tenant");
    } finally {
      setSubmitting(false);
    }
  };

  const inp = "w-full h-9 px-3 rounded-md border text-sm bg-white";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="new-tenant-modal">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--cw-border)" }}>
          <h2 className="cw-h2">New Tenant</h2>
          <button type="button" onClick={onClose} data-testid="close-tenant-modal"><X size={20} /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          <div className="cw-caption uppercase tracking-widest">Tenant</div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Tenant Name *">
              <input required value={form.tenant_name} onChange={(e) => set("tenant_name", e.target.value)}
                className={inp} style={{ borderColor: "var(--cw-border)" }} data-testid="tenant-name-input" />
            </F>
            <F label="Slug (unique) *">
              <input required value={form.tenant_slug}
                onChange={(e) => set("tenant_slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                className={inp} style={{ borderColor: "var(--cw-border)" }}
                data-testid="tenant-slug-input" placeholder="my-vc-firm" />
            </F>
            <F label="Country">
              <input value={form.country} onChange={(e) => set("country", e.target.value)}
                className={inp} style={{ borderColor: "var(--cw-border)" }} />
            </F>
            <F label="City">
              <input value={form.city} onChange={(e) => set("city", e.target.value)}
                className={inp} style={{ borderColor: "var(--cw-border)" }} />
            </F>
            <F label="Subscription Tier">
              <select value={form.subscription_tier} onChange={(e) => set("subscription_tier", e.target.value)}
                className={inp} style={{ borderColor: "var(--cw-border)" }}>
                <option value="free">Free</option>
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
              </select>
            </F>
            <F label="Currency">
              <select value={form.primary_currency} onChange={(e) => set("primary_currency", e.target.value)}
                className={inp} style={{ borderColor: "var(--cw-border)" }}>
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </F>
          </div>

          <div className="cw-caption uppercase tracking-widest mt-4">First Fund</div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <F label="Fund Name *">
                <input required value={form.fund_name} onChange={(e) => set("fund_name", e.target.value)}
                  className={inp} style={{ borderColor: "var(--cw-border)" }} data-testid="fund-name-input" />
              </F>
            </div>
            <F label="Vintage Year">
              <input type="number" value={form.vintage_year} onChange={(e) => set("vintage_year", e.target.value)}
                className={inp} style={{ borderColor: "var(--cw-border)" }} />
            </F>
            <div className="col-span-3">
              <F label="Fund Size (native currency)">
                <input value={form.fund_size_inr} onChange={(e) => set("fund_size_inr", e.target.value)}
                  className={inp} style={{ borderColor: "var(--cw-border)" }} placeholder="500000000" />
              </F>
            </div>
          </div>

          <div className="cw-caption uppercase tracking-widest mt-4">First VC Admin</div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Full Name *">
              <input required value={form.admin_full_name} onChange={(e) => set("admin_full_name", e.target.value)}
                className={inp} style={{ borderColor: "var(--cw-border)" }} data-testid="admin-name-input" />
            </F>
            <F label="Email *">
              <input type="email" required value={form.admin_email}
                onChange={(e) => set("admin_email", e.target.value.toLowerCase())}
                className={inp} style={{ borderColor: "var(--cw-border)" }} data-testid="admin-email-input" />
            </F>
            <div className="col-span-2">
              <F label="Temporary Password * (min 6 chars)">
                <input type="password" required minLength={6} value={form.admin_password}
                  onChange={(e) => set("admin_password", e.target.value)}
                  className={inp} style={{ borderColor: "var(--cw-border)" }} data-testid="admin-password-input" />
              </F>
            </div>
          </div>

          <div className="cw-caption uppercase tracking-widest mt-4">Workspace Options</div>
          <div className="space-y-2">
            <label className="flex items-start gap-2 text-sm" data-testid="multi-pipeline-toggle-wrap">
              <input
                type="checkbox"
                checked={form.multi_pipeline_enabled}
                onChange={(e) => set("multi_pipeline_enabled", e.target.checked)}
                className="mt-1"
                data-testid="multi-pipeline-toggle"
              />
              <span>
                <span className="font-medium">Allow multiple pipelines</span>
                <div className="text-xs text-[var(--cw-muted)]">
                  When off, VC Admin can create only one pipeline.
                </div>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm" data-testid="seed-sample-toggle-wrap">
              <input
                type="checkbox"
                checked={form.seed_sample_data}
                onChange={(e) => set("seed_sample_data", e.target.checked)}
                className="mt-1"
                data-testid="seed-sample-toggle"
              />
              <span>
                <span className="font-medium">Seed this tenant with sample data</span>
                <div className="text-xs text-[var(--cw-muted)]">
                  Creates 3 sample deals, 1 draft gate, and 3 tasks owned by the first VC Admin.
                </div>
              </span>
            </label>
          </div>
        </div>
        <div className="border-t px-6 py-4 flex justify-end gap-2" style={{ borderColor: "var(--cw-border)" }}>
          <button type="button" onClick={onClose} className="cw-btn-ghost">Cancel</button>
          <button type="submit" disabled={submitting} className="cw-btn-primary" data-testid="submit-tenant-btn">
            {submitting ? "Provisioning…" : "Create Tenant"}
          </button>
        </div>
      </form>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="cw-caption block mb-1">{label}</label>
      {children}
    </div>
  );
}
